import { z } from 'zod'

const normalizedPointSchema = z
  .strictObject({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
  })
  .transform(({ x, y }) => ({ normalized_x: x, normalized_y: y }))
const polylineSchema = z.strictObject({
  type: z.literal('polyline'),
  points: z.array(normalizedPointSchema).min(2),
})
const polygonSchema = z.strictObject({
  type: z.literal('polygon'),
  points: z.array(normalizedPointSchema).min(3),
})
const tagsSchema = z.strictObject({
  sandbox: z.literal('sandbox'),
  organization: z.string(),
  site: z.string(),
  'system-document': z.string().nullable().optional(),
  'system-document-page': z.string().nullable().optional(),
  'pipeline-config': z.string().nullable().optional(),
})
const pageTagsSchema = tagsSchema.extend({ 'system-document-page': z.string() })
const base = { id: z.string(), tags: tagsSchema }
const appearance = {
  page_id: z.string(),
  coordinate_space: z.literal('page_normalized'),
}
export const pnidNodeSchema = z.looseObject({
  ...base,
  type: z.literal('pnid-node'),
  data: z.strictObject({
    dexpi_class: z.string(),
    dexpi_model_object_ref: z.string().nullable().optional(),
    attributes: z.record(z.string(), z.json()),
  }),
})
export const pnidDexpiEdgeSchema = z.looseObject({
  ...base,
  type: z.literal('pnid-edge'),
  data: z.strictObject({
    type: z.literal('dexpi'),
    source_node_id: z.string(),
    target_node_id: z.string(),
    dexpi_relationship_field: z.string(),
    relationship_index: z.number().int().nonnegative().nullable().optional(),
  }),
})
export const pnidDatapointEdgeSchema = pnidDexpiEdgeSchema.extend({
  data: z.strictObject({
    type: z.literal('datapoint_association'),
    source_node_id: z.string(),
    target_node_id: z.string(),
  }),
})
export const pnidEdgeSchema = pnidDexpiEdgeSchema.extend({
  data: z.discriminatedUnion('type', [
    pnidDexpiEdgeSchema.shape.data,
    pnidDatapointEdgeSchema.shape.data,
  ]),
})
export const pnidNodeOverlaySchema = z.looseObject({
  ...base,
  tags: pageTagsSchema,
  type: z.literal('pnid-node-overlay'),
  data: z.strictObject({
    ...appearance,
    node_id: z.string(),
    geometry: z.discriminatedUnion('type', [polygonSchema, polylineSchema]),
  }),
})
export const pnidProgressSchema = z.looseObject({
  ...base,
  tags: pageTagsSchema,
  type: z.literal('pnid-extraction-progress'),
  data: z.strictObject({
    page_id: z.string(),
    status: z.enum([
      'unprocessed',
      'in_progress',
      'complete',
      'cannot_complete',
    ]),
    progress_notes: z.string(),
  }),
})
export const pnidEntrySchema = z
  .discriminatedUnion('type', [
    pnidNodeSchema,
    pnidEdgeSchema,
    pnidNodeOverlaySchema,
    pnidProgressSchema,
  ])
  .refine(
    (entry) =>
      !('page_id' in entry.data) ||
      entry.data.page_id === entry.tags['system-document-page'],
    // eslint-disable-next-line lingui/no-unlocalized-strings -- Internal schema invariant; invalid entries are discarded before reaching the UI.
    { error: 'Overlay page and page tag must match' }
  )

export type PnidEntry = z.infer<typeof pnidEntrySchema>
export type PnidNode = z.infer<typeof pnidNodeSchema>
export type PnidEdge = z.infer<typeof pnidEdgeSchema>
export type PnidDexpiEdge = z.infer<typeof pnidDexpiEdgeSchema>
export type PnidDatapointEdge = z.infer<typeof pnidDatapointEdgeSchema>
export type PnidNodeOverlay = z.infer<typeof pnidNodeOverlaySchema>
export type NormalizedPoint = z.infer<typeof normalizedPointSchema>
export type NormalizedGeometry = PnidNodeOverlay['data']['geometry']
